/**
 * Biometric Backup for Vault PIN
 * 
 * Uses Web Authentication API (WebAuthn) for biometric authentication
 * combined with secure local storage for the vault data.
 * 
 * Flow:
 * 1. Setup: User creates biometric credential + vault data stored encrypted locally
 * 2. Recovery: User authenticates with biometric + vault data retrieved from local storage
 */

const STORAGE_KEY = 'vault_biometric_data';
const CREDENTIAL_KEY = 'vault_biometric_credential_id';

// Check if biometric/credential storage is available
export const isBiometricAvailable = async () => {
  try {
    // Check for PublicKeyCredential (WebAuthn) support
    if (window.PublicKeyCredential) {
      // Check if platform authenticator (FaceID/TouchID) is available
      const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      console.log('WebAuthn platform authenticator available:', available);
      return available;
    }
    console.log('WebAuthn not supported');
    return false;
  } catch (error) {
    console.error('Biometric check error:', error);
    return false;
  }
};

// Generate a random challenge for WebAuthn
const generateChallenge = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return array;
};

// Convert ArrayBuffer to Base64
const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  return btoa(binary);
};

// Convert Base64 to ArrayBuffer  
const base64ToBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Simple encryption for local storage (not meant to be highly secure - biometric is the gate)
const encryptForStorage = (data, credentialId) => {
  const json = JSON.stringify(data);
  // XOR with credential ID for basic obfuscation
  const key = credentialId.slice(0, 32).padEnd(32, 'x');
  let encrypted = '';
  for (let i = 0; i < json.length; i++) {
    encrypted += String.fromCharCode(json.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return btoa(encrypted);
};

const decryptFromStorage = (encrypted, credentialId) => {
  const key = credentialId.slice(0, 32).padEnd(32, 'x');
  const decoded = atob(encrypted);
  let decrypted = '';
  for (let i = 0; i < decoded.length; i++) {
    decrypted += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return JSON.parse(decrypted);
};

/**
 * Store vault backup using biometric authentication
 * @param {string} userId - User's unique ID
 * @param {string} vaultSalt - The vault salt to backup
 * @param {string} pin - The PIN to backup
 */
export const setupBiometricBackup = async (userId, vaultSalt, pin) => {
  try {
    // First check if WebAuthn is available
    const isAvailable = await isBiometricAvailable();
    if (!isAvailable) {
      console.log('Biometrics not available, using simple backup');
      return setupSimpleBiometricBackup(vaultSalt, pin);
    }

    const challenge = generateChallenge();
    
    // Create a clean user ID buffer (iOS requires this to be properly formatted)
    const cleanUserId = userId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 32) || 'healthvault';
    const userIdBuffer = new TextEncoder().encode(cleanUserId);

    console.log('Creating WebAuthn credential for biometric backup...');
    console.log('RP ID:', window.location.hostname);

    const publicKeyCredentialCreationOptions = {
      challenge: challenge,
      rp: {
        name: "EmergencyCard Health",
        id: window.location.hostname
      },
      user: {
        id: userIdBuffer,
        name: cleanUserId,
        displayName: "Health Vault Backup"
      },
      pubKeyCredParams: [
        { alg: -7, type: "public-key" },   // ES256 (preferred for iOS)
        { alg: -257, type: "public-key" }  // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: "platform", // Use built-in authenticator (Face ID/Touch ID)
        userVerification: "required",
        residentKey: "discouraged" // Changed from "required" - more compatible
      },
      timeout: 120000, // 2 minutes for iOS which can be slow
      attestation: "none"
    };

    const credential = await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions
    });

    if (credential) {
      const credentialId = bufferToBase64(credential.rawId);
      console.log('Credential created successfully:', credentialId.slice(0, 20) + '...');
      
      // Store credential ID
      localStorage.setItem(CREDENTIAL_KEY, credentialId);
      
      // Store vault data encrypted with credential ID
      const vaultData = { salt: vaultSalt, pin: pin, timestamp: Date.now() };
      const encryptedData = encryptForStorage(vaultData, credentialId);
      localStorage.setItem(STORAGE_KEY, encryptedData);
      
      localStorage.setItem('vault_biometric_enabled', 'true');
      
      return { success: true, credentialId, method: 'webauthn' };
    }
    
    return { success: false, error: 'Failed to create credential' };
  } catch (error) {
    console.error('Biometric setup error:', error.name, error.message);
    
    // Specific error handling
    if (error.name === 'NotAllowedError') {
      return { success: false, error: 'Face ID / Touch ID was cancelled or denied. Please try again.' };
    }
    if (error.name === 'SecurityError') {
      // This often happens on iOS when not using HTTPS or wrong RP ID
      console.log('SecurityError - falling back to simple backup');
      return setupSimpleBiometricBackup(vaultSalt, pin);
    }
    if (error.name === 'NotSupportedError') {
      console.log('NotSupportedError - falling back to simple backup');
      return setupSimpleBiometricBackup(vaultSalt, pin);
    }
    if (error.name === 'InvalidStateError') {
      // Credential already exists - this is actually okay
      console.log('Credential may already exist, storing vault data');
      const existingCredId = localStorage.getItem(CREDENTIAL_KEY);
      if (existingCredId) {
        const vaultData = { salt: vaultSalt, pin: pin, timestamp: Date.now() };
        const encryptedData = encryptForStorage(vaultData, existingCredId);
        localStorage.setItem(STORAGE_KEY, encryptedData);
        localStorage.setItem('vault_biometric_enabled', 'true');
        return { success: true, credentialId: existingCredId, method: 'existing' };
      }
    }
    
    return { success: false, error: error.message || 'Biometric setup failed' };
  }
};

/**
 * Recover vault using biometric authentication
 * @returns {object} - The recovered vault data (salt, pin)
 */
export const recoverWithBiometric = async () => {
  try {
    const storedCredentialId = localStorage.getItem(CREDENTIAL_KEY);
    const encryptedData = localStorage.getItem(STORAGE_KEY);
    
    if (!storedCredentialId || !encryptedData) {
      console.log('No biometric backup found, trying simple backup');
      return recoverFromSimpleBackup();
    }

    if (!window.PublicKeyCredential) {
      throw new Error('WebAuthn not supported');
    }

    const challenge = generateChallenge();
    
    console.log('Requesting biometric authentication...');

    // Request authentication with the stored credential
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge: challenge,
        rpId: window.location.hostname,
        allowCredentials: [{
          id: base64ToBuffer(storedCredentialId),
          type: 'public-key',
          transports: ['internal']
        }],
        userVerification: "required",
        timeout: 60000
      }
    });

    if (assertion) {
      console.log('Biometric authentication successful');
      
      // Decrypt and return the vault data
      const vaultData = decryptFromStorage(encryptedData, storedCredentialId);
      return { success: true, data: vaultData };
    }

    return { success: false, error: 'Authentication failed' };
  } catch (error) {
    console.error('Biometric recovery error:', error);
    
    if (error.name === 'NotAllowedError') {
      return { success: false, error: 'Biometric authentication was cancelled or denied' };
    }
    if (error.name === 'SecurityError') {
      return { success: false, error: 'Security error - please try again' };
    }
    
    // Try simple backup as fallback
    console.log('WebAuthn failed, trying simple backup');
    return recoverFromSimpleBackup();
  }
};

/**
 * Check if biometric backup is enabled for this user
 */
export const isBiometricBackupEnabled = () => {
  const enabled = localStorage.getItem('vault_biometric_enabled') === 'true';
  const hasData = localStorage.getItem(STORAGE_KEY) !== null || localStorage.getItem('vault_backup_data') !== null;
  return enabled && hasData;
};

/**
 * Disable biometric backup
 */
export const disableBiometricBackup = () => {
  localStorage.removeItem(CREDENTIAL_KEY);
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem('vault_biometric_enabled');
  localStorage.removeItem('vault_backup_data');
};

/**
 * Simple fallback for browsers without WebAuthn or when WebAuthn fails
 * This stores encrypted backup in localStorage
 * Less secure but works everywhere as a fallback
 */
export const setupSimpleBiometricBackup = (vaultSalt, pin) => {
  try {
    // Generate a simple encryption key from device info
    const deviceKey = navigator.userAgent.slice(0, 32).padEnd(32, 'k');
    const backupData = JSON.stringify({ 
      salt: vaultSalt, 
      pin: pin, 
      timestamp: Date.now(),
      device: navigator.userAgent.slice(0, 50)
    });
    
    // Simple XOR encryption
    let encrypted = '';
    for (let i = 0; i < backupData.length; i++) {
      encrypted += String.fromCharCode(backupData.charCodeAt(i) ^ deviceKey.charCodeAt(i % deviceKey.length));
    }
    
    localStorage.setItem('vault_backup_data', btoa(encrypted));
    localStorage.setItem('vault_biometric_enabled', 'true');
    
    console.log('Simple biometric backup created');
    return { success: true, method: 'simple' };
  } catch (error) {
    console.error('Simple backup error:', error);
    return { success: false, error: error.message };
  }
};

export const recoverFromSimpleBackup = () => {
  try {
    const backupData = localStorage.getItem('vault_backup_data');
    if (!backupData) {
      return { success: false, error: 'No backup found' };
    }
    
    const deviceKey = navigator.userAgent.slice(0, 32).padEnd(32, 'k');
    const decoded = atob(backupData);
    
    let decrypted = '';
    for (let i = 0; i < decoded.length; i++) {
      decrypted += String.fromCharCode(decoded.charCodeAt(i) ^ deviceKey.charCodeAt(i % deviceKey.length));
    }
    
    const data = JSON.parse(decrypted);
    console.log('Simple backup recovered successfully');
    return { success: true, data };
  } catch (error) {
    console.error('Simple backup recovery error:', error);
    return { success: false, error: 'Invalid or corrupted backup data' };
  }
};
